// Presupuesto de la oferta — tabla editable tipo plantilla Excel
// ("2027_Propuesta estructura tarifas" · hoja Ejemplo CIEC).
//
// Lizzi la propone como un paso del análisis completo (una llamada IA dedicada,
// _shared/budget-pipeline.ts) y el equipo la revisa, edita y completa aquí.
// Los importes salen del tarifario editable (página Tarifario). Se guarda en
// bids.go_data.budget con autoguardado; la versión anterior a una regeneración
// queda en go_data.budgetPrevious para poder deshacerla.
import React from 'react';
import { Icon } from '../icons.jsx';
import { notify } from '../modals.jsx';
import { fetchBid, generateBudget, waitBudgetJob, saveBudget, swapBudgetVersion } from '../data/repo.js';
import { useBudgetCatalog } from '../data/budget-catalog.js';
import { xlsxBlob, sheetRef, colName } from '../data/exportXlsx.js';
import { BudgetDrawer, parseSources } from './budget-drawer.jsx';
import { EXTERNAL_COST_FACTOR } from '../../supabase/functions/_shared/budget-catalog.js';
import { computeRow, computeTotals, tariffSnapshot, toNum } from '../../supabase/functions/_shared/budget-calc.js';
import {
  fmtEur, fmtEur0, fmtPct, normalize, timeAgo, round2, UNIT_SHORT,
  TagPill, TagPicker, TariffPicker, NumField, TextField, tagTone,
} from './budget-ui.jsx';

const { useState, useEffect, useMemo, useRef, useCallback } = React;

const FACTOR_TXT = String(EXTERNAL_COST_FACTOR).replace('.', ',');

// Procedencia de cada importe: tarifario, estimación de la IA o edición manual.
const SOURCE_META = {
  tarifario:  { label: 'Tarifario',             hint: 'Importe del tarifario interno' },
  estimacion: { label: 'Estimación de Lizzi',   hint: 'Coste estimado por Lizzi: revísalo' },
  calculado:  { label: `Coste × ${FACTOR_TXT}`, hint: `Sin tarifa: coste unitario × ${FACTOR_TXT}` },
  manual:     { label: 'Fijado a mano',         hint: 'Importe fijado por el equipo' },
  pendiente:  { label: 'Sin importe',           hint: 'Falta el coste: complétalo' },
};

const ORIGIN_META = {
  pliego:    { label: 'Pliego',    hint: 'Necesidad explícita en los pliegos' },
  implicito: { label: 'Implícito', hint: 'Coste deducido de las obligaciones del pliego' },
  manual:    { label: 'Manual',    hint: 'Partida añadida por el equipo' },
};

const FILTERS = [
  { id: 'all', label: 'Todas' },
  { id: 'review', label: 'Por revisar' },
  { id: 'pliego', label: 'Del pliego' },
  { id: 'implicito', label: 'Implícitas' },
  { id: 'manual', label: 'Manuales' },
];

const COLS = 13;

// Una partida necesita revisión si su coste lo ha estimado la IA o falta, y nadie la ha validado.
function needsReview(r, c) {
  return !r.reviewed && (c.costSource === 'estimacion' || c.costSource === 'pendiente');
}

function newRow(patch = {}) {
  return {
    concept: '', units: 1, tariffId: '', tariffSnap: null, tag: '',
    unitCostEstimate: null, unitCostManual: null, unitPriceManual: null,
    discountPrice: null, discountCost: null,
    origin: 'manual', source: '', rationale: '', reviewed: false,
    ...patch,
    id: `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
  };
}

const readPref = (k, d) => { try { return localStorage.getItem(k) || d; } catch { return d; } };
const writePref = (k, v) => { try { localStorage.setItem(k, v); } catch { /* sin almacenamiento */ } };

function toCsv(rows, catalog) {
  const head = ['Concepto', 'Uds/h', 'Código tarifario', 'Concepto interno', 'Etiqueta', 'Tarifa unitaria', 'Reducción tarifa %', 'Total', 'Coste unitario', 'Reducción coste %', 'Total coste', 'Margen %', 'Origen', 'Procedencia coste', 'Fuente', 'Justificación'];
  const num = (n) => (n == null || !Number.isFinite(n) ? '' : String(round2(n)).replace('.', ','));
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) => {
    const c = computeRow(r, catalog.byId);
    const margin = c.total > 0 ? ((c.total - c.totalCost) / c.total) * 100 : null;
    return [r.concept, num(c.units), r.tariffId, catalog.byId[r.tariffId]?.name || r.tariffSnap?.name || '', r.tag, num(c.unitPrice), num(toNum(r.discountPrice)), num(c.total),
      num(c.unitCost), num(toNum(r.discountCost)), num(c.totalCost), num(margin), ORIGIN_META[r.origin]?.label || '', SOURCE_META[c.costSource]?.label || '',
      r.source, r.rationale].map(esc).join(';');
  });
  return '﻿' + [head.map(esc).join(';'), ...lines].join('\n');
}

// Libro Excel: hoja «Presupuesto» con las partidas y las mismas fórmulas que la
// plantilla (para seguir trabajando en Excel) y hoja «Resumen» con los totales,
// el reparto por etiqueta y los supuestos de Lizzi.
function toXlsx(rows, catalog, { bid, budget } = {}) {
  const SHEET = 'Presupuesto';
  const T = `${sheetRef(SHEET)}!`;
  const head = ['Concepto', 'Uds/h', 'Código tarifario', 'Concepto interno', 'Etiqueta', 'Tarifa unitaria', 'Reducción tarifa', 'Total', 'Coste unitario', 'Reducción coste', 'Total coste', 'Margen', 'Origen', 'Procedencia coste', 'Por revisar', 'Fuente', 'Justificación', 'Nota'];
  const txt = (v) => ({ v: v || '', s: 'cell' });
  const eur = (v) => ({ v, s: 'eur' });
  const pct = (v) => { const n = toNum(v); return { v: n == null ? null : n / 100, s: 'pct' }; };
  const first = 2;
  const last = rows.length + 1;
  const tot = last + 1;
  let reviewCount = 0;
  const data = rows.map((r, i) => {
    const n = first + i;
    const c = computeRow(r, catalog.byId);
    const review = needsReview(r, c);
    if (review) reviewCount += 1;
    return [
      txt(r.concept), { v: c.units, s: 'cell' }, txt(r.tariffId), txt(catalog.byId[r.tariffId]?.name || r.tariffSnap?.name), txt(r.tag),
      c.priceSource === 'calculado' ? { f: `I${n}*${EXTERNAL_COST_FACTOR}`, v: c.unitPrice, s: 'eur' } : eur(c.unitPrice),
      pct(r.discountPrice), { f: `B${n}*F${n}*(1-G${n})`, v: c.total, s: 'eur' },
      eur(c.unitCost), pct(r.discountCost), { f: `B${n}*I${n}*(1-J${n})`, v: c.totalCost, s: 'eur' },
      { f: `IF(H${n}>0,(H${n}-K${n})/H${n},"")`, v: c.total > 0 ? (c.total - c.totalCost) / c.total : '', s: 'pct' },
      txt(ORIGIN_META[r.origin]?.label), txt(SOURCE_META[c.costSource]?.label), txt(review ? 'Sí' : 'No'),
      txt(r.source), { v: r.rationale || '', s: 'wrap' }, { v: r.note || '', s: 'wrap' },
    ];
  });
  const totals = computeTotals(rows, catalog.byId);
  const totalRow = head.map(() => ({ s: 'total' }));
  totalRow[0] = { v: `Total · ${rows.length} partida${rows.length === 1 ? '' : 's'}`, s: 'total' };
  totalRow[7] = { f: `SUM(H${first}:H${last})`, v: totals.total, s: 'totalEur' };
  totalRow[10] = { f: `SUM(K${first}:K${last})`, v: totals.totalCost, s: 'totalEur' };
  totalRow[11] = { f: `IF(H${tot}>0,(H${tot}-K${tot})/H${tot},"")`, v: totals.marginPct == null ? '' : totals.marginPct / 100, s: 'totalPct' };

  // Resumen: las fórmulas leen la hoja de partidas, así que siguen los cambios hechos en Excel.
  const sum = [];
  const add = (...cells) => sum.push(cells);
  const label = (v) => ({ v, s: 'bold' });
  const base = Number(bid?.budget_base) || 0;
  add({ v: 'Presupuesto de la oferta', s: 'title' });
  add();
  add(label('Licitación'), txt(bid?.title));
  add(label('Expediente'), txt(bid?.code));
  add(label('Organismo'), txt(bid?.entity));
  add(label('Exportado'), txt(new Date().toLocaleString('es-ES', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })));
  add();
  const rBase = add(label('Presupuesto base (sin IVA)'), base > 0 ? eur(base) : txt('Sin presupuesto base en la ficha'));
  const rTotal = add(label('Total oferta (sin IVA)'), { f: `${T}H${tot}`, v: totals.total, s: 'eur' });
  const rCost = add(label('Coste total'), { f: `${T}K${tot}`, v: totals.totalCost, s: 'eur' });
  const rMargin = add(label('Margen bruto'), { f: `B${rTotal}-B${rCost}`, v: totals.margin, s: 'eur' });
  add(label('Margen %'), { f: `IF(B${rTotal}>0,B${rMargin}/B${rTotal},"")`, v: totals.marginPct == null ? '' : totals.marginPct / 100, s: 'pct' });
  if (base > 0) add(label('% del presupuesto base'), { f: `B${rTotal}/B${rBase}`, v: totals.total / base, s: 'pct' });
  add(label('Partidas'), { v: rows.length, s: 'cell' });
  add(label('Por revisar'), { f: `COUNTIF(${T}O${first}:O${last},"Sí")`, v: reviewCount, s: 'cell' });
  add();
  add(...['Etiqueta', 'Partidas', 'Total', 'Coste', 'Margen', '% del total'].map((v) => ({ v, s: 'head' })));
  Object.entries(totals.byTag).sort((a, b) => b[1].total - a[1].total).forEach(([tag, t]) => {
    const n = sum.length + 1;
    const match = `(${T}$E$${first}:$E$${last}=${tag === 'Sin etiqueta' ? '""' : `A${n}`})`;
    add(txt(tag), { f: `SUMPRODUCT(${match}*1)`, v: t.count, s: 'cell' },
      { f: `SUMPRODUCT(${match}*${T}$H$${first}:$H$${last})`, v: t.total, s: 'eur' },
      { f: `SUMPRODUCT(${match}*${T}$K$${first}:$K$${last})`, v: t.totalCost, s: 'eur' },
      { f: `C${n}-D${n}`, v: t.total - t.totalCost, s: 'eur' },
      { f: `IF(B$${rTotal}>0,C${n}/B$${rTotal},"")`, v: totals.total > 0 ? t.total / totals.total : '', s: 'pct' });
  });
  if (Array.isArray(budget?.assumptions) && budget.assumptions.length) {
    add();
    add(label('Supuestos de Lizzi'));
    budget.assumptions.forEach((s) => add(txt(s)));
  }

  return xlsxBlob([
    { name: SHEET, cols: [44, 9, 12, 32, 30, 14, 10, 15, 14, 10, 15, 9, 11, 20, 10, 30, 60, 40],
      rows: [head.map((v) => ({ v, s: 'head' })), ...data, totalRow], freeze: { rows: 1, cols: 1 }, autoFilter: `A1:${colName(head.length - 1)}${last}`, fitWidth: true },
    { name: 'Resumen', cols: [34, 16, 16, 16, 16, 12], rows: sum },
  ]);
}

// ---------- Resumen ----------
function Summary({ totals, budgetBase, reviewCount, rowsCount, mode, setMode }) {
  const usage = budgetBase ? (totals.total / budgetBase) * 100 : null;
  const over = budgetBase > 0 && totals.total > budgetBase;
  return (
    <div className="card bb-sum">
      <div className="bb-sum-tile">
        <div className="bb-eyebrow">Total oferta · sin IVA</div>
        <div className="bb-sum-value">{fmtEur0(totals.total)}</div>
        {budgetBase > 0 ? (
          <>
            <div className="bb-ceiling" title={`${fmtPct(usage)} del presupuesto base (${fmtEur(budgetBase)})`}>
              <span className={`bb-ceiling-fill${over ? ' is-over' : ''}`} style={{ width: `${Math.min(100, usage)}%` }}/>
            </div>
            <div className={`bb-sum-sub${over ? ' is-over' : ''}`} title={`Presupuesto base: ${fmtEur(budgetBase)}`}>
              {over ? `+${fmtEur0(totals.total - budgetBase)} sobre el presupuesto base` : `${fmtPct(usage)} del presupuesto base`}
            </div>
          </>
        ) : <div className="bb-sum-sub">Sin presupuesto base en la ficha</div>}
      </div>
      <div className="bb-sum-tile">
        <div className="bb-eyebrow">Coste total</div>
        <div className="bb-sum-value">{fmtEur0(totals.totalCost)}</div>
        <div className="bb-sum-sub">Interno + externo</div>
      </div>
      <div className="bb-sum-tile">
        <div className="bb-eyebrow">Margen bruto</div>
        <div className={`bb-sum-value${totals.margin < 0 ? ' is-neg' : ''}`}>{fmtEur0(totals.margin)}</div>
        <div className="bb-sum-sub">{fmtPct(totals.marginPct)} · antes de overhead</div>
      </div>
      <button type="button" className={`bb-sum-tile is-click${mode === 'review' ? ' is-active' : ''}`} disabled={!reviewCount}
        onClick={() => setMode(mode === 'review' ? 'all' : 'review')}>
        <div className="bb-eyebrow">Por revisar</div>
        <div className={`bb-sum-value${reviewCount ? ' is-warn' : ' is-ok'}`}>
          {reviewCount ? <>{reviewCount}<span className="bb-sum-of"> de {rowsCount}</span></> : 'Todo revisado'}
        </div>
        <div className={`bb-sum-sub${reviewCount ? ' is-link' : ''}`}>
          {reviewCount ? (mode === 'review' ? 'Ver todas las partidas' : `${totals.pending ? `${totals.pending} sin importe · ` : ''}Revisar →`) : 'Ninguna estimación pendiente'}
        </div>
      </button>
    </div>
  );
}

// Composición por etiqueta: una magnitud (lo que se cobra) → barras ordenadas de
// un solo tono; el punto de color y el nombre aportan la identidad.
function Composition({ byTag, total, activeTag, onPick, tagByName }) {
  const [all, setAll] = useState(false);
  const items = Object.entries(byTag).filter(([, v]) => v.total > 0 || v.totalCost > 0).sort((a, b) => b[1].total - a[1].total);
  if (!items.length) return null;
  const shown = all ? items : items.slice(0, 8);
  const max = Math.max(...items.map(([, v]) => v.total), 1);
  return (
    <div className="card bb-comp">
      <div className="bb-comp-head">
        <div className="bb-eyebrow">Dónde va el presupuesto</div>
        <span>Total a cobrar por etiqueta · clic para filtrar</span>
      </div>
      <div className="bb-comp-list">
        {shown.map(([tag, v]) => {
          const share = total > 0 ? (v.total / total) * 100 : 0;
          const isActive = activeTag === tag;
          return (
            <button key={tag} type="button" className={`bb-bar${isActive ? ' is-active' : ''}${activeTag && !isActive ? ' is-dim' : ''}`}
              onClick={() => onPick(isActive ? null : tag)}
              title={`${tag}\nA cobrar: ${fmtEur(v.total)} (${fmtPct(share)})\nCoste: ${fmtEur(v.totalCost)}\nMargen: ${fmtEur(v.total - v.totalCost)} · ${v.count} partida${v.count === 1 ? '' : 's'}`}>
              <span className="bb-bar-label"><span className={`tg-dot tg-${tagTone(tag === 'Sin etiqueta' ? '' : tag, tagByName)}`}/>{tag}</span>
              <span className="bb-bar-track"><span className="bb-bar-fill" style={{ width: `${Math.max(1.5, (v.total / max) * 100)}%` }}/></span>
              <span className="bb-bar-value">{fmtEur0(v.total)}</span>
              <span className="bb-bar-share">{fmtPct(share, 0)}</span>
            </button>
          );
        })}
      </div>
      {items.length > 8 && (
        <button className="bb-link" style={{ marginTop: 8 }} onClick={() => setAll((a) => !a)}>
          {all ? 'Ver menos' : `Ver las ${items.length} etiquetas`}
        </button>
      )}
    </div>
  );
}

// ---------- Celdas ----------
function SourceDot({ source, stale }) {
  const meta = SOURCE_META[source] || SOURCE_META.pendiente;
  return <span className={`src-dot is-${source}${stale ? ' is-stale' : ''}`} title={stale ? 'El tarifario ha cambiado desde que se eligió esta partida' : meta.hint}/>;
}

function MoneyCell({ value, source, manual, stale, live, onChange, onReset, disabled, cost }) {
  return (
    <td className={`num${cost ? ' is-cost' : ''}`}>
      <div className="money-cell">
        <SourceDot source={source} stale={stale}/>
        <NumField value={value == null ? null : round2(value)} disabled={disabled} ariaLabel={cost ? 'Coste unitario' : 'Tarifa unitaria'}
          invalid={source === 'pendiente'} placeholder={source === 'pendiente' ? 'Falta' : '—'}
          onCommit={(v) => (v == null ? (manual != null && onReset()) : onChange(v))}/>
        {manual != null && !disabled && (
          <button className="row-icon reset" title="Volver al importe calculado" onClick={onReset}><Icon.Refresh size={11}/></button>
        )}
      </div>
      {stale && live != null && <div className="money-stale">tarifario: {fmtEur(live)}</div>}
    </td>
  );
}

function BudgetRow({ r, c, idx, total, catalog, locked, filtered, selected, onOpen, update, changeTariff, moveRow, duplicateRow, delRow }) {
  const t = catalog.byId[r.tariffId];
  const review = needsReview(r, c);
  const unit = t ? (UNIT_SHORT[t.unit] || t.unit) : '';
  const margin = c.total > 0 ? ((c.total - c.totalCost) / c.total) * 100 : null;
  const origin = ORIGIN_META[r.origin] || ORIGIN_META.manual;
  const src = parseSources(r.source)[0];
  const srcLabel = src ? `${src.doc.replace(/\.pdf$/i, '')}${src.pages ? ` · p. ${src.pages}` : ''}` : '';
  return (
    <>
      <tr data-row-id={r.id} className={`bb-row${review ? ' needs-review' : ''}${c.costSource === 'pendiente' ? ' is-pending' : ''}${selected ? ' is-selected' : ''}`}>
        <td className="bb-stick-1 bb-idx">{idx + 1}</td>
        <td className="bb-stick-2 bb-concept">
          <TextField value={r.concept} strong disabled={locked} placeholder="¿Qué hay que presupuestar?" ariaLabel="Concepto"
            onCommit={(v) => update(r.id, { concept: v })}/>
          <button type="button" className="bb-meta" onClick={onOpen} title="Ver el detalle de la partida">
            {review
              ? <span className={`bb-meta-review${c.costSource === 'pendiente' ? ' is-danger' : ''}`}>{c.costSource === 'pendiente' ? 'Falta el coste' : 'Estimación de Lizzi · por validar'}</span>
              : <span className={`bb-origin is-${r.origin || 'manual'}`} title={origin.hint}>{origin.label}</span>}
            {srcLabel && <span className="bb-src">{srcLabel}</span>}
            {r.note && <span className="bb-has-note" title={r.note}><Icon.MessageSquare size={11}/></span>}
          </button>
        </td>
        <td className="num">
          <NumField value={r.units} disabled={locked} ariaLabel="Unidades u horas" suffix={unit || undefined} placeholder="0"
            onCommit={(v) => update(r.id, { units: v ?? 0 })}/>
        </td>
        <td>
          <TariffPicker value={r.tariffId} catalog={catalog} disabled={locked} snapName={r.tariffSnap?.name} onChange={(id) => changeTariff(r, id)}/>
        </td>
        <td>
          <TagPicker value={r.tag} tags={catalog.tags} tagByName={catalog.tagByName} disabled={locked} onChange={(v) => update(r.id, { tag: v })}/>
        </td>
        <MoneyCell value={c.unitPrice} source={c.priceSource} manual={toNum(r.unitPriceManual)} disabled={locked}
          stale={c.priceStale} live={c.liveRate}
          onChange={(v) => update(r.id, { unitPriceManual: v })} onReset={() => update(r.id, { unitPriceManual: null })}/>
        <td className="num">
          <NumField value={r.discountPrice} placeholder="–" suffix={r.discountPrice != null ? '%' : undefined} disabled={locked} ariaLabel="Reducción sobre la tarifa (%)"
            onCommit={(v) => update(r.id, { discountPrice: v == null ? null : Math.min(100, v) })}/>
        </td>
        <td className="num bb-total">{fmtEur(c.total)}</td>
        <MoneyCell cost value={c.unitCost} source={c.costSource} manual={toNum(r.unitCostManual)} disabled={locked}
          stale={c.costStale} live={c.liveCost}
          onChange={(v) => update(r.id, { unitCostManual: v, reviewed: true })} onReset={() => update(r.id, { unitCostManual: null })}/>
        <td className="num is-cost">
          <NumField value={r.discountCost} placeholder="–" suffix={r.discountCost != null ? '%' : undefined} disabled={locked} ariaLabel="Reducción sobre el coste (%)"
            onCommit={(v) => update(r.id, { discountCost: v == null ? null : Math.min(100, v) })}/>
        </td>
        <td className="num is-cost bb-total">{fmtEur(c.totalCost)}</td>
        <td className="num"><span className={`bb-margin${margin != null && margin < 0 ? ' is-neg' : ''}`}>{fmtPct(margin, 0)}</span></td>
        <td className="bb-actions-cell">
          <div className="bb-actions-wrap">
          {!locked && (
            <div className="row-actions">
              <button className="row-icon" title="Subir" disabled={idx === 0 || filtered} onClick={() => moveRow(r.id, -1)}><Icon.ChevronUp size={13}/></button>
              <button className="row-icon" title="Bajar" disabled={idx === total - 1 || filtered} onClick={() => moveRow(r.id, 1)}><Icon.ChevronDown size={13}/></button>
              <button className="row-icon" title="Duplicar" onClick={() => duplicateRow(r)}><Icon.Copy size={12}/></button>
              <button className="row-icon is-danger" title="Eliminar" onClick={() => delRow(r.id)}><Icon.Trash size={12}/></button>
            </div>
          )}
          <button className={`row-icon bb-open${selected ? ' is-on' : ''}`} onClick={onOpen} aria-label="Ver detalle" title="Ver detalle"><Icon.ChevronRight size={15}/></button>
          </div>
        </td>
      </tr>
    </>
  );
}

// Fila de subtotal (cabecera de grupo o pie de tabla) alineada con las columnas de importes.
function SubtotalCells({ t }) {
  return (
    <>
      <td className="num bb-total">{fmtEur(t.total)}</td>
      <td colSpan={2} className="is-cost"/>
      <td className="num is-cost bb-total">{fmtEur(t.totalCost)}</td>
      <td className="num"><span className={`bb-margin${t.margin < 0 ? ' is-neg' : ''}`}>{fmtPct(t.marginPct, 0)}</span></td>
      <td/>
    </>
  );
}

// ---------- Componente principal ----------
function BudgetBuilder({ bid }) {
  const catalog = useBudgetCatalog();
  const initial = (bid && bid.go_data) || {};
  const [budget, setBudget] = useState(initial.budget || null);
  const [previous, setPrevious] = useState(initial.budgetPrevious || null);
  const [rows, setRows] = useState((initial.budget && initial.budget.rows) || []);
  const [job, setJob] = useState(initial.budgetJob || null);
  const [generating, setGenerating] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [saveState, setSaveState] = useState('saved'); // saved | dirty | saving | error
  const [savedAt, setSavedAt] = useState(initial.budget?.updatedAt || null);
  const [openId, setOpenId] = useState(null);
  const [mode, setMode] = useState('all');
  const [tagFilter, setTagFilter] = useState(null);
  const [q, setQ] = useState('');
  const [view, setViewState] = useState(() => readPref('lizzi:budget-view', 'grouped'));
  const [collapsed, setCollapsed] = useState({});
  const [focusId, setFocusId] = useState(null);
  const [, tick] = useState(0);

  const setView = (v) => { setViewState(v); writePref('lizzi:budget-view', v); };

  const rowsRef = useRef(rows);
  const budgetRef = useRef(budget);
  const dirtyRef = useRef(false);
  const aliveRef = useRef(true);
  rowsRef.current = rows;
  budgetRef.current = budget;

  const apply = (row) => {
    const g = row?.go_data || {};
    setBudget(g.budget || null);
    setPrevious(g.budgetPrevious || null);
    setRows((g.budget && g.budget.rows) || []);
    setJob(g.budgetJob || null);
    setSavedAt(g.budget?.updatedAt || null);
    setSaveState('saved');
    dirtyRef.current = false;
    setOpenId(null);
    setMode('all'); setTagFilter(null); setQ('');
  };

  const follow = async (promise) => {
    setGenerating(true); setElapsed(0);
    try {
      const row = await promise;
      if (!aliveRef.current) return;
      apply(row);
      const st = row?.go_data?.budgetJob?.status;
      if (st === 'done') notify('Presupuesto propuesto por Lizzi. Revisa las partidas marcadas.', 'CheckCircle');
    } catch (e) {
      if (!aliveRef.current) return;
      setJob({ status: 'error', error: e?.message || 'error' });
      notify('No se pudo generar el presupuesto: ' + (e?.message || 'error'), 'AlertTriangle');
    } finally { if (aliveRef.current) setGenerating(false); }
  };

  // Persistencia (autoguardado con espera corta tras cada edición).
  const persist = useCallback(async () => {
    if (!dirtyRef.current || !bid?.id) return;
    dirtyRef.current = false;
    setSaveState('saving');
    try {
      const next = { ...(budgetRef.current || { version: 1 }), rows: rowsRef.current };
      await saveBudget(bid.id, next);
      const now = new Date().toISOString();
      if (aliveRef.current) {
        setBudget({ ...next, updatedAt: now, editedAt: now });
        setSavedAt(now);
        setSaveState(dirtyRef.current ? 'dirty' : 'saved');
      }
    } catch (e) {
      dirtyRef.current = true;
      if (aliveRef.current) setSaveState('error');
      notify('No se pudo guardar el presupuesto: ' + (e?.message || 'error'), 'AlertTriangle');
    }
  }, [bid?.id]);

  useEffect(() => {
    if (saveState !== 'dirty') return undefined;
    const t = setTimeout(persist, 1200);
    return () => clearTimeout(t);
  }, [saveState, rows, persist]);

  // Al salir con cambios sin guardar, se guardan igualmente.
  useEffect(() => () => { aliveRef.current = false; if (dirtyRef.current) persist(); }, [persist]);

  // Refresca el "hace X min".
  useEffect(() => { const i = setInterval(() => tick((n) => n + 1), 30_000); return () => clearInterval(i); }, []);

  // Al montar: relee go_data (la prop puede estar desfasada) y retoma una generación en curso.
  useEffect(() => {
    if (!bid?.id) return undefined;
    aliveRef.current = true;
    fetchBid(bid.id).then((row) => {
      if (!aliveRef.current || !row) return;
      if (!dirtyRef.current) apply(row);
      const j = row.go_data?.budgetJob;
      const fresh = j?.startedAt && Date.now() - new Date(j.startedAt).getTime() < 10 * 60_000;
      if (j?.status === 'processing' && fresh) follow(waitBudgetJob(bid.id, { onTick: (ms) => aliveRef.current && setElapsed(ms) }));
    }).catch(() => {});
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bid?.id]);

  // Edición
  const mutate = (fn) => {
    setRows((rs) => fn(rs));
    dirtyRef.current = true;
    setSaveState('dirty');
  };
  const update = (id, patch) => mutate((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const changeTariff = (r, tariffId) => {
    const prevDefault = catalog.byId[r.tariffId]?.tag || '';
    const nextDefault = catalog.byId[tariffId]?.tag || '';
    const patch = { tariffId, tariffSnap: tariffId ? tariffSnapshot(tariffId, catalog.byId) : null };
    if (nextDefault && (!r.tag || r.tag === prevDefault)) patch.tag = nextDefault;
    update(r.id, patch);
  };
  const refreshTariffs = () => {
    const n = rows.filter((r) => computeRow(r, catalog.byId).tariffStale).length;
    mutate((rs) => rs.map((r) => (computeRow(r, catalog.byId).tariffStale ? { ...r, tariffSnap: tariffSnapshot(r.tariffId, catalog.byId) } : r)));
    notify(`${n} partida${n === 1 ? '' : 's'} actualizada${n === 1 ? '' : 's'} con el tarifario vigente`, 'Refresh');
  };
  const addRow = (patch = {}) => {
    const r = newRow({ ...(tagFilter && tagFilter !== 'Sin etiqueta' ? { tag: tagFilter } : {}), ...patch });
    mutate((rs) => [...rs, r]);
    setMode('all'); setQ('');
    setFocusId(r.id);
  };
  const duplicateRow = (r) => {
    const copy = newRow({ ...r, origin: 'manual', concept: `${r.concept} (copia)` });
    mutate((rs) => { const i = rs.findIndex((x) => x.id === r.id); const out = rs.slice(); out.splice(i + 1, 0, copy); return out; });
    setFocusId(copy.id);
  };
  const delRow = (id) => {
    const idx = rowsRef.current.findIndex((r) => r.id === id);
    const removed = rowsRef.current[idx];
    mutate((rs) => rs.filter((r) => r.id !== id));
    notify(`Partida eliminada: ${removed?.concept || 'sin concepto'}`, 'Trash');
  };
  const moveRow = (id, dir) => mutate((rs) => {
    const i = rs.findIndex((r) => r.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= rs.length) return rs;
    const out = rs.slice();
    [out[i], out[j]] = [out[j], out[i]];
    return out;
  });
  const markAllReviewed = () => mutate((rs) => rs.map((r) => {
    const c = computeRow(r, catalog.byId);
    return needsReview(r, c) && c.costSource !== 'pendiente' ? { ...r, reviewed: true } : r;
  }));

  useEffect(() => {
    if (!focusId) return;
    const el = document.querySelector(`[data-row-id="${focusId}"] input`);
    if (el) { el.focus(); el.select?.(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); }
    setFocusId(null);
  }, [focusId, rows]);

  // IA
  const runAi = async () => {
    if (dirtyRef.current) await persist();
    follow(generateBudget(bid.id, { onTick: (ms) => aliveRef.current && setElapsed(ms) }));
  };
  const swapVersion = async () => {
    if (!previous) return;
    if (dirtyRef.current) await persist();
    try {
      const cur = { ...(budgetRef.current || {}), rows: rowsRef.current };
      const row = await swapBudgetVersion(bid.id, cur, previous);
      apply({ go_data: row.goData });
      notify('Versión restaurada. Puedes volver a la otra con el mismo botón.', 'Refresh');
    } catch (e) {
      notify('No se pudo restaurar: ' + (e?.message || 'error'), 'AlertTriangle');
    }
  };

  const download = (blob, ext) => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `presupuesto-${(bid?.code || bid?.id || 'licitacion').toString().replace(/[^\w.-]+/g, '_')}.${ext}`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const exportCsv = () => download(new Blob([toCsv(rows, catalog)], { type: 'text/csv;charset=utf-8' }), 'csv');
  const exportXlsx = () => download(toXlsx(rows, catalog, { bid, budget }), 'xlsx');

  // Derivados
  const computed = useMemo(() => {
    const map = {};
    for (const r of rows) map[r.id] = computeRow(r, catalog.byId);
    return map;
  }, [rows, catalog]);
  const totals = useMemo(() => computeTotals(rows, catalog.byId), [rows, catalog]);
  const reviewCount = rows.filter((r) => needsReview(r, computed[r.id])).length;
  const counts = {
    all: rows.length,
    review: reviewCount,
    pliego: rows.filter((r) => r.origin === 'pliego').length,
    implicito: rows.filter((r) => r.origin === 'implicito').length,
    manual: rows.filter((r) => r.origin === 'manual').length,
  };
  const nq = normalize(q);
  const visible = rows.filter((r) => {
    if (mode === 'review' && !needsReview(r, computed[r.id])) return false;
    if (['pliego', 'implicito', 'manual'].includes(mode) && r.origin !== mode) return false;
    if (tagFilter && (r.tag || 'Sin etiqueta') !== tagFilter) return false;
    if (nq && !normalize(`${r.concept} ${r.tariffId} ${catalog.byId[r.tariffId]?.name || ''} ${r.tag} ${r.source}`).includes(nq)) return false;
    return true;
  });
  const filtered = visible.length !== rows.length;
  const visibleTotals = filtered ? computeTotals(visible, catalog.byId) : totals;

  // Agrupado por etiqueta, en el orden de las etiquetas del tarifario.
  const groups = useMemo(() => {
    if (view !== 'grouped') return null;
    const order = new Map(catalog.tags.map((t, i) => [t.name, i]));
    const m = new Map();
    visible.forEach((r) => { const k = r.tag || 'Sin etiqueta'; if (!m.has(k)) m.set(k, []); m.get(k).push(r); });
    return [...m.entries()].sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999));
  }, [view, visible, catalog.tags]);

  // Orden de navegación del panel de detalle: el mismo que se ve en la tabla.
  const navRows = groups ? groups.flatMap(([, items]) => items) : visible;
  const openIdx = openId ? navRows.findIndex((r) => r.id === openId) : -1;
  const openRow = openIdx >= 0 ? navRows[openIdx] : null;
  const goTo = useCallback((r) => {
    if (!r) return;
    const tag = r.tag || 'Sin etiqueta';
    setCollapsed((c) => (c[tag] ? { ...c, [tag]: false } : c));
    setOpenId(r.id);
  }, []);
  useEffect(() => {
    if (!openId) return;
    const el = document.querySelector(`[data-row-id="${CSS.escape(openId)}"]`);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openId]);
  useEffect(() => { if (openId && openIdx < 0 && !rows.some((r) => r.id === openId)) setOpenId(null); }, [openId, openIdx, rows]);
  // El detalle se integra en la pestaña: columna junto a la tabla si hay ancho,
  // o ficha desplegada bajo la partida si la pestaña es estrecha.
  const rootRef = useRef(null);
  const wrapRef = useRef(null);
  const [rootW, setRootW] = useState(0);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(() => {
      setRootW(rootRef.current?.clientWidth || 0);
      setWrapW(wrapRef.current?.clientWidth || 0);
    });
    if (rootRef.current) ro.observe(rootRef.current);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [rows.length > 0]);
  const docked = rootW >= 1080;
  const validateNext = () => {
    if (!openRow) return;
    update(openRow.id, { reviewed: true });
    const after = [...navRows.slice(openIdx + 1), ...navRows.slice(0, openIdx)];
    const nextReview = after.find((r) => r.id !== openRow.id && needsReview(r, computed[r.id]));
    goTo(nextReview || navRows[openIdx + 1] || null);
    if (!nextReview) notify('No quedan estimaciones por validar', 'CheckCircle');
  };

  const budgetBase = Number(bid?.budget_base) || 0;
  const locked = generating;

  const saveLabel = {
    saving: <><span className="bb-dot is-busy"/> Guardando…</>,
    dirty: <><span className="bb-dot is-busy"/> Cambios sin guardar</>,
    error: <><span className="bb-dot is-error"/> Error al guardar · <button className="bb-link" onClick={persist}>Reintentar</button></>,
    saved: <><span className="bb-dot"/> Guardado {savedAt ? timeAgo(savedAt) : ''}</>,
  }[saveState];

  const provenance = budget?.generatedAt
    ? `Propuesto por Lizzi ${budget.trigger === 'analysis' ? 'en el análisis ' : ''}el ${new Date(budget.generatedAt).toLocaleString('es-ES', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}${budget.editedAt ? ' · editado por el equipo' : ''}`
    : rows.length ? 'Presupuesto creado por el equipo' : null;

  const detail = (mode) => openRow && (
    <BudgetDrawer mode={mode} row={openRow} c={computed[openRow.id]} position={openIdx + 1} total={navRows.length} catalog={catalog} locked={locked}
      needsReview={needsReview} onClose={() => setOpenId(null)}
      onPrev={() => goTo(navRows[openIdx - 1])} onNext={() => goTo(navRows[openIdx + 1])} onValidateNext={validateNext}
      update={update} changeTariff={changeTariff} duplicateRow={duplicateRow}
      delRow={(id) => { const nxt = navRows[openIdx + 1] || navRows[openIdx - 1]; delRow(id); if (nxt && nxt.id !== id) setOpenId(nxt.id); else setOpenId(null); }}/>
  );

  const renderRow = (r) => {
    const idx = rows.indexOf(r);
    const inline = !docked && openId === r.id;
    return (
      <React.Fragment key={r.id}>
      <BudgetRow r={r} c={computed[r.id]} idx={idx} total={rows.length} catalog={catalog} locked={locked} filtered={filtered || view === 'grouped'}
        selected={openId === r.id} onOpen={() => setOpenId(openId === r.id ? null : r.id)}
        update={update} changeTariff={changeTariff} moveRow={moveRow} duplicateRow={duplicateRow} delRow={delRow}/>
      {inline && (
        <tr className="bb-inline-row">
          <td colSpan={COLS}>
            <div className="bb-inline" style={wrapW ? { width: wrapW - 2 } : undefined}>{detail('inline')}</div>
          </td>
        </tr>
      )}
      </React.Fragment>
    );
  };

  return (
    <div className="bb" ref={rootRef}>
      {/* Cabecera */}
      <div className="bb-head">
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="bb-head-icon"><Icon.Calculator size={15}/></span>
            <div className="font-display" style={{ fontSize: 20, lineHeight: 1.2 }}>Presupuesto de la oferta</div>
          </div>
          <div className="bb-head-sub">
            {provenance && <span>{provenance}</span>}
            {rows.length > 0 && <span className="bb-save">{saveLabel}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {previous?.rows?.length > 0 && !locked && (
            <button className="btn btn-ghost" onClick={swapVersion} title="Intercambia con la versión anterior a la última regeneración">
              <Icon.Refresh size={14}/> Versión anterior
            </button>
          )}
          {rows.length > 0 && (
            <button className="btn btn-outline" onClick={exportXlsx} title="Libro Excel con las fórmulas de la plantilla, totales y resumen por etiqueta">
              <Icon.Download size={14}/> Exportar Excel
            </button>
          )}
          {rows.length > 0 && <button className="btn btn-ghost" onClick={exportCsv} title="Exportar CSV"><Icon.Download size={14}/> CSV</button>}
          {rows.length > 0 && (
            <button className="btn btn-outline" onClick={runAi} disabled={locked} title="Lizzi vuelve a leer los pliegos y propone una tabla nueva. Podrás volver a esta versión.">
              <Icon.Sparkles size={14}/> Regenerar con Lizzi
            </button>
          )}
        </div>
      </div>

      {/* Estados de la IA */}
      {generating && (
        <div className="callout callout-info bb-shimmer">
          <div className="callout-icon"><Icon.Sparkles size={16}/></div>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>
              {job?.trigger === 'analysis' ? 'Lizzi está preparando el presupuesto como parte del análisis…' : 'Lizzi está leyendo los pliegos y preparando las partidas…'}
            </div>
            <div style={{ fontSize: 12.5 }}>
              Identifica necesidades explícitas e implícitas, las cruza con el tarifario y estima lo que no tiene tarifa. Suele tardar 1-3 min
              {elapsed > 0 && <> · {Math.round(elapsed / 1000)} s</>}. Puedes seguir trabajando en otras pestañas.
            </div>
          </div>
        </div>
      )}
      {!generating && job?.status === 'error' && (
        <div className="callout callout-warn">
          <div className="callout-icon"><Icon.AlertTriangle size={16}/></div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, marginBottom: 2 }}>No se pudo preparar la propuesta de presupuesto</div>
            <div style={{ fontSize: 12.5 }}>{job.error}</div>
          </div>
          <button className="btn btn-outline" onClick={runAi}><Icon.Refresh size={14}/> Reintentar</button>
        </div>
      )}
      {!generating && job?.status === 'skipped' && job?.reason === 'edited' && rows.length > 0 && (
        <div className="bb-notice">
          <Icon.Info size={14}/>
          <span>El último análisis no ha sustituido este presupuesto porque el equipo ya lo había editado. Para una propuesta nueva usa <strong>Regenerar con Lizzi</strong> (podrás volver a esta versión).</span>
        </div>
      )}

      {/* Estado vacío */}
      {!rows.length && !generating && (
        <div className="card bb-empty">
          <span className="bb-head-icon is-lg"><Icon.Calculator size={20}/></span>
          <div className="font-display" style={{ fontSize: 18, marginTop: 14 }}>Prepara el presupuesto con Lizzi</div>
          <p>
            Lizzi lee los pliegos, detecta las partidas que pide el contrato (y las que implica aunque no las nombre), las vincula al tarifario interno y estima el coste de lo que no tiene tarifa. Después lo revisas y ajustas aquí.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={runAi}><Icon.Sparkles size={14}/> Generar propuesta</button>
            <button className="btn btn-outline" onClick={() => addRow()}><Icon.Plus size={14}/> Empezar en blanco</button>
          </div>
        </div>
      )}

      {rows.length > 0 && (
        <>
            <Summary totals={totals} budgetBase={budgetBase} reviewCount={reviewCount} rowsCount={rows.length} mode={mode} setMode={setMode}/>
            <Composition byTag={totals.byTag} total={totals.total} activeTag={tagFilter} onPick={setTagFilter} tagByName={catalog.tagByName}/>

          {/* Avisos */}
          {(totals.stale > 0 || budget?.warnings?.length > 0) && (
            <div className="bb-notices">
              {totals.stale > 0 && !locked && (
                <div className="bb-notice is-warn">
                  <Icon.Refresh size={14}/>
                  <span><strong>{totals.stale} partida{totals.stale === 1 ? '' : 's'}</strong> usa{totals.stale === 1 ? '' : 'n'} importes anteriores del tarifario (marcadas con <span className="src-dot is-tarifario is-stale" style={{ verticalAlign: 'middle' }}/>).</span>
                  <button className="btn btn-outline btn-sm" onClick={refreshTariffs}>Actualizar al tarifario vigente</button>
                </div>
              )}
              {(budget?.warnings || []).map((w, i) => (
                <div key={i} className="bb-notice is-warn"><Icon.AlertTriangle size={14}/><span>{w}</span></div>
              ))}
            </div>
          )}

          {/* Barra de herramientas */}
          <div className="bb-toolbar">
            <div className="bb-toolbar-left">
              <div className="bb-seg" role="tablist">
                {FILTERS.map((f) => (
                  <button key={f.id} role="tab" aria-selected={mode === f.id} className={mode === f.id ? 'is-active' : ''} onClick={() => setMode(f.id)}
                    disabled={f.id !== 'all' && !counts[f.id]}>
                    {f.label}<span className="bb-seg-count">{counts[f.id]}</span>
                  </button>
                ))}
              </div>
              {tagFilter && (
                <span className="bb-chip-filter">
                  <TagPill tag={tagFilter} tone={tagTone(tagFilter === 'Sin etiqueta' ? '' : tagFilter, catalog.tagByName)} size="sm"/>
                  <button className="row-icon" onClick={() => setTagFilter(null)} title="Quitar filtro"><Icon.X size={11}/></button>
                </span>
              )}
              {mode === 'review' && reviewCount > 0 && (
                <button className="btn btn-ghost btn-sm" onClick={markAllReviewed} title="Marca como revisadas las estimaciones de Lizzi que ya tienen importe">
                  <Icon.Check size={13}/> Validar todas las estimaciones
                </button>
              )}
            </div>
            <div className="bb-toolbar-right">
              <div className="bb-seg is-icons" role="tablist" aria-label="Vista">
                <button className={view === 'grouped' ? 'is-active' : ''} onClick={() => setView('grouped')} title="Agrupar por etiqueta"><Icon.Layers size={13}/> Por etiqueta</button>
                <button className={view === 'list' ? 'is-active' : ''} onClick={() => setView('list')} title="Orden de la propuesta"><Icon.Layout size={13}/> Lista</button>
              </div>
              <div className="bb-search">
                <Icon.Search size={13}/>
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar partida"/>
                {q && <button className="row-icon" onClick={() => setQ('')}><Icon.X size={11}/></button>}
              </div>
              <button className="btn btn-primary" onClick={() => addRow()} disabled={locked}><Icon.Plus size={14}/> Añadir partida</button>
            </div>
          </div>

          {/* Tabla (+ detalle acoplado a la derecha en pantallas anchas) */}
          <div className={`bb-split${docked && openRow ? ' has-panel' : ''}`}>
          <div ref={wrapRef} className={`card bb-table-wrap${locked ? ' is-locked' : ''}`}>
            <table className="bb-table">
              <colgroup>
                <col style={{ width: 44 }}/><col style={{ width: 340 }}/><col style={{ width: 112 }}/><col style={{ width: 230 }}/><col style={{ width: 210 }}/>
                <col style={{ width: 120 }}/><col style={{ width: 72 }}/><col style={{ width: 128 }}/>
                <col style={{ width: 120 }}/><col style={{ width: 72 }}/><col style={{ width: 128 }}/>
                <col style={{ width: 76 }}/><col style={{ width: 112 }}/>
              </colgroup>
              <thead>
                <tr className="bb-th-band">
                  <th className="bb-stick-1"/>
                  <th className="bb-stick-2"/>
                  <th colSpan={3}/>
                  <th colSpan={3} className="band-sale">Venta</th>
                  <th colSpan={3} className="band-cost is-cost">Coste</th>
                  <th colSpan={2}/>
                </tr>
                <tr>
                  <th className="bb-stick-1"/>
                  <th className="bb-stick-2">Concepto</th>
                  <th className="num">Uds/h</th>
                  <th>Concepto interno</th>
                  <th>Etiqueta</th>
                  <th className="num">Tarifa unitaria</th>
                  <th className="num" title="Reducción o descuento sobre la tarifa">Red.</th>
                  <th className="num">Total</th>
                  <th className="num is-cost">Coste unitario</th>
                  <th className="num is-cost" title="Reducción o descuento sobre el coste">Red.</th>
                  <th className="num is-cost">Total coste</th>
                  <th className="num" title="(Total − Total coste) / Total">Margen</th>
                  <th/>
                </tr>
              </thead>
              <tbody>
                {groups ? groups.map(([tag, items]) => {
                  const gt = computeTotals(items, catalog.byId);
                  const isCol = !!collapsed[tag];
                  return (
                    <React.Fragment key={tag}>
                      <tr className="bb-group-row">
                        <td className="bb-stick-1"/>
                        <td className="bb-stick-2" colSpan={1}>
                          <button className="bb-group-toggle" onClick={() => setCollapsed((c) => ({ ...c, [tag]: !isCol }))} aria-expanded={!isCol}>
                            <Icon.ChevronDown size={13} style={{ transform: isCol ? 'rotate(-90deg)' : 'none', transition: 'transform 150ms ease' }}/>
                            <TagPill tag={tag === 'Sin etiqueta' ? '' : tag} tone={tagTone(tag === 'Sin etiqueta' ? '' : tag, catalog.tagByName)}/>
                            <span className="bb-group-count">{items.length}</span>
                          </button>
                        </td>
                        <td colSpan={5} className="bb-group-share">{gt.total > 0 && totals.total > 0 ? `${fmtPct((gt.total / totals.total) * 100)} del total` : ''}</td>
                        <SubtotalCells t={gt}/>
                      </tr>
                      {!isCol && items.map(renderRow)}
                      {!isCol && !locked && tag !== 'Sin etiqueta' && (
                        <tr className="bb-add-row">
                          <td className="bb-stick-1"/>
                          <td className="bb-stick-2" colSpan={1}><button className="bb-add-btn" onClick={() => addRow({ tag })}><Icon.Plus size={12}/> Añadir en {tag}</button></td>
                          <td colSpan={COLS - 2}/>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }) : visible.map(renderRow)}
                {!visible.length && (
                  <tr><td colSpan={COLS} className="bb-empty-row">
                    Ninguna partida coincide con el filtro. <button className="bb-link" onClick={() => { setMode('all'); setTagFilter(null); setQ(''); }}>Ver todas</button>
                  </td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bb-foot">
                  <td className="bb-stick-1"/>
                  <td className="bb-stick-2">
                    {filtered ? `Subtotal · ${visible.length} de ${rows.length} partidas` : `Total · ${rows.length} partida${rows.length === 1 ? '' : 's'}`}
                  </td>
                  <td colSpan={5}>
                    {!locked && view === 'list' && <button className="bb-link" onClick={() => addRow()}><Icon.Plus size={11}/> Añadir partida</button>}
                  </td>
                  <SubtotalCells t={visibleTotals}/>
                </tr>
              </tfoot>
            </table>
          </div>
          {docked && detail('docked')}
          </div>

          {/* Supuestos y método */}
          <div className="bb-bottom">
            {Array.isArray(budget?.assumptions) && budget.assumptions.length > 0 && (
              <div className="card bb-note">
                <div className="bb-note-title"><Icon.Sparkles size={13}/> Supuestos de Lizzi · confírmalos con el equipo</div>
                <ul>{budget.assumptions.map((s, i) => <li key={i}>{s}</li>)}</ul>
              </div>
            )}
            <div className="card bb-note">
              <div className="bb-note-title"><Icon.Info size={13}/> Cómo se calcula</div>
              <p><strong>Total</strong> = Uds/h × tarifa unitaria × (1 − red.) · <strong>Total coste</strong> = Uds/h × coste unitario × (1 − red.)</p>
              <p>Con partida del tarifario, tarifa y coste salen de la página <a href="/tariff">Tarifario</a> (se guardan en la fila al elegirla). Sin tarifa, el coste lo estima Lizzi (o tú) y la tarifa es coste × {FACTOR_TXT}. Cualquier importe se puede fijar a mano.</p>
              <div className="bb-legend">
                {['tarifario', 'estimacion', 'calculado', 'manual', 'pendiente'].map((k) => (
                  <span key={k}><SourceDot source={k}/>{SOURCE_META[k].label}</span>
                ))}
                <span><SourceDot source="tarifario" stale/>Tarifario cambiado</span>
              </div>
            </div>
          </div>
        </>
      )}

    </div>
  );
}

export { BudgetBuilder };
